import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Select,
  Button,
  Alert,
  Badge,
  Paper,
  Loader,
  Title
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useApiClient } from '../../api';

const SubscriptionManager = ({ opened, onClose, userId, username }) => {
  const api = useApiClient();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  // Either the {plan, status} nested object from the admin user view, or the
  // full Subscription resource returned by a management action.
  const [subscription, setSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');

  const plans = [
    { value: 'paypiggie', label: 'PayPiggie ($15/month)' },
    { value: 'paypiggiepro', label: 'PayPiggiePro ($25/month)' }
  ];

  // There is no "get" action on the subscription route — the current
  // subscription is read from the admin user detail (nested object).
  const fetchSubscription = useCallback(async () => {
    try {
      setFetching(true);
      setError(null);
      const user = await api.get(`/api/v1/admin/users/${userId}`);
      setSubscription(user.subscription);
      if (user.subscription) {
        setSelectedPlan(user.subscription.plan);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err.message || 'Failed to fetch subscription');
    } finally {
      setFetching(false);
    }
  }, [api, userId]);

  useEffect(() => {
    if (opened && userId) {
      fetchSubscription();
    }
  }, [opened, userId, fetchSubscription]);

  const runAction = async (body, successMessage, color, icon) => {
    try {
      setLoading(true);
      setError(null);

      const updated = await api.put(`/api/v1/admin/users/${userId}/subscription`, body);
      setSubscription(updated);
      if (updated?.plan) setSelectedPlan(updated.plan);

      notifications.show({
        title: 'Success',
        message: successMessage,
        color,
        icon
      });
    } catch (err) {
      console.error('Error managing subscription:', err);
      setError(err.message || 'Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateSubscription = () => {
    if (!selectedPlan) {
      setError('Please select a subscription plan');
      return;
    }
    return runAction(
      { action: 'activate', plan: selectedPlan },
      'Subscription activated successfully',
      'green',
      <IconCheck size="1rem" />
    );
  };

  const handleCancelSubscription = () => runAction(
    { action: 'cancel' },
    'Subscription canceled successfully',
    'yellow',
    <IconX size="1rem" />
  );

  const handleExpireSubscription = () => runAction(
    { action: 'expire' },
    'Subscription expired successfully',
    'red',
    <IconX size="1rem" />
  );

  const getStatusColor = (status) => {
    const colors = {
      active: 'green',
      canceled: 'yellow',
      expired: 'red',
      pending: 'gray'
    };
    return colors[status] || 'gray';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Manage Subscription - ${username}`}
      size="lg"
    >
      <Stack>
        {error && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="red">
            {error}
          </Alert>
        )}

        {fetching ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Loader />
          </div>
        ) : (
          <>
            {/* Current Subscription Status */}
            <Paper p="md" withBorder>
              <Title order={5} mb="sm">Current Subscription</Title>
              {subscription ? (
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text>Plan:</Text>
                    <Badge size="lg" variant="filled">
                      {subscription.plan === 'paypiggie' ? 'PayPiggie' : 'PayPiggiePro'}
                    </Badge>
                  </Group>
                  <Group justify="space-between">
                    <Text>Status:</Text>
                    <Badge color={getStatusColor(subscription.status)} variant="light">
                      {subscription.status}
                    </Badge>
                  </Group>
                  {subscription.price_cents != null && (
                    <Group justify="space-between">
                      <Text>Price:</Text>
                      <Text fw={600}>${(subscription.price_cents / 100).toFixed(2)}/month</Text>
                    </Group>
                  )}
                  {subscription.current_period_start && (
                    <Group justify="space-between">
                      <Text>Period Start:</Text>
                      <Text>{formatDate(subscription.current_period_start)}</Text>
                    </Group>
                  )}
                  {subscription.current_period_end && (
                    <Group justify="space-between">
                      <Text>Period End:</Text>
                      <Text>{formatDate(subscription.current_period_end)}</Text>
                    </Group>
                  )}
                  {subscription.canceled_at && (
                    <Group justify="space-between">
                      <Text>Canceled At:</Text>
                      <Text>{formatDate(subscription.canceled_at)}</Text>
                    </Group>
                  )}
                </Stack>
              ) : (
                <Text c="dimmed" ta="center" py="md">
                  No subscription
                </Text>
              )}
            </Paper>

            {/* Subscription Management */}
            <Paper p="md" withBorder>
              <Title order={5} mb="sm">Manage Subscription</Title>

              <Stack>
                <Select
                  label="Select Plan"
                  placeholder="Choose a subscription plan"
                  data={plans}
                  value={selectedPlan}
                  onChange={setSelectedPlan}
                  disabled={loading}
                />

                <Group grow>
                  <Button
                    onClick={handleActivateSubscription}
                    loading={loading}
                    disabled={!selectedPlan}
                    color="green"
                  >
                    {subscription ? 'Update' : 'Activate'} Subscription
                  </Button>
                </Group>

                {subscription && ['active', 'past_due'].includes(subscription.status) && (
                  <Group grow>
                    <Button
                      onClick={handleCancelSubscription}
                      loading={loading}
                      color="yellow"
                      variant="light"
                    >
                      Cancel Subscription
                    </Button>
                    <Button
                      onClick={handleExpireSubscription}
                      loading={loading}
                      color="red"
                      variant="light"
                    >
                      Expire Now
                    </Button>
                  </Group>
                )}
              </Stack>
            </Paper>

            <Text size="xs" c="dimmed" ta="center">
              Note: Admin-managed subscriptions bypass payment processing
            </Text>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default SubscriptionManager;
